import type { VerlaufEintrag } from "../v3/verlauf";
import type { DocumentDto } from "../../../services/documentService";

/**
 * Was der Rechtsklick auf einer EREIGNISKARTE anbietet (PO 14.09.2026:
 * „Dokument per Rechtsklick umbenennen“, „Mails, Anrufe, Dokumente per
 * Rechtsklick entfernen“) — und mit welchem Patch.
 *
 * REINE Ableitung, bewusst ohne React und ohne Fetch (Muster
 * `blockZuweisung.ts`): die Regeln hier kosten Daten, wenn sie falsch sind,
 * und gehören deshalb an EINEN Ort mit einem Test statt als Objektliteral in
 * die Seite.
 *
 * ══ ENTFERNEN = LÖSEN ══════════════════════════════════════════════════════
 * Eine Mail liegt in Graph, ein Anruf in der Telefonie, ein Termin im
 * Kalender — der Vorgang hat davon nur die VERKNÜPFUNG. „Aus dem Vorgang
 * entfernen“ nimmt genau die: `DELETE /mail/links/:id`, `PATCH
 * /telephony/calls/:id { vorgangId: null, todoId: null }`, `PUT /documents/:id
 * { vorgangId: null, todoId: null, … }`. Nur das Dokument gehört KMVS selbst;
 * es allein trägt daneben den schärferen Punkt „Löschen“
 * (`DELETE /documents/:id`, seit E2 vom 16.09.2026 der Papierkorb).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Welche Punkte in welcher Reihenfolge erscheinen, steht nebenan in
 * {@link ./ereignisMenue}; hier liegen nur die Patches und die Texte.
 */

/**
 * Dateiname in Basis und Endung — die Endung bleibt beim Umbenennen fix,
 * weil das Backend die Extension des Bestands ohnehin erzwingt
 * (`sanitizeRenameFileName`); ein editierbares „.pdf“ würde stumm korrigiert.
 * Ein führender Punkt („.htaccess“) ist keine Endung.
 */
export function splitExt(name: string): { base: string; ext: string } {
    const dot = name.lastIndexOf(".");
    return dot > 0 ? { base: name.slice(0, dot), ext: name.slice(dot) } : { base: name, ext: "" };
}

/**
 * Der Patch, mit dem ein Dokument den Vorgang verlässt.
 *
 * ══ ES BLEIBT BEIM KUNDEN ══════════════════════════════════════════════════
 * V4-Uploads tragen nur `vorgangId` ODER `todoId`, nie einen Kontakt
 * (`aktionsKnoepfe.tsx`). Nähme der Patch nur die beiden Bezüge, wäre das
 * Dokument danach bezugslos — aus keiner Liste mehr erreichbar, obwohl nicht
 * gelöscht. Deshalb wandert es an den Kunden des Vorgangs; ein schon
 * gesetzter `contactId` (Ablage-Fall) hat Vorrang. Ohne Kunden (persönlicher
 * Vorgang) bleibt `contactId` null — die Rückfrage sagt das dann dazu.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function dokumentLoesenPatch(
    dokument: Pick<DocumentDto, "contactId">,
    kundeId: number | null | undefined,
): { vorgangId: null; todoId: null; contactId: number | null } {
    return { vorgangId: null, todoId: null, contactId: dokument.contactId ?? kundeId ?? null };
}

/**
 * Der Patch für „→ zurück an den Vorgang“ bei Dokument und Anruf.
 *
 * Nebenbefund vom 14.09.2026: ein im TO-DO hochgeladenes Dokument trägt nur
 * `todoId`. `{ todoId: null }` allein ließ es damit komplett aus dem Vorgang
 * fallen — die Vorgangsliste findet ein Dokument über `vorgangId` ODER über
 * das To-do, und beides war danach weg. Der Vorgangsbezug wird deshalb
 * explizit mitgeschickt; für einen Anruf gilt dieselbe Rechnung
 * (`call.vorgangId = X OR call.todoId IN (To-dos von X)`). Bei einer Mail
 * macht das Backend das von sich aus (`MailLinkService.setTodo`).
 */
export function zurueckAnVorgangPatch(vorgangId: number): { todoId: null; vorgangId: number } {
    return { todoId: null, vorgangId };
}

/**
 * Die Rückfrage vor dem Entfernen (eigenes Rückfrage-Popup): ein
 * Mail-Link ist hart gelöscht, kein Papierkorb — ein Fehlklick ließe sich nur
 * durch erneutes Verknüpfen aus dem Postfach zurückholen. Der zweite Satz
 * sagt, was NICHT passiert, damit niemand aus Sorge um die Datei zögert.
 */
export function entfernenRueckfrage(
    eintrag: Pick<VerlaufEintrag, "art">,
    opts: {
        bleibtBeimKunden: boolean;
        /**
         * Gesetzt, wenn dieser Vorgang das Dokument nur zusätzlich anzeigt:
         * der Vorgang, in dem es zu Hause ist, oder `null` für ein Dokument
         * aus der Kundenakte. Fehlt die Angabe, ist es ein eigenes Dokument.
         */
        heimat?: { vorgangNbr: string | null } | null;
    },
): string {
    switch (eintrag.art) {
        case "mail":
            return "Diese Mail aus dem Vorgang entfernen? Die Mail selbst bleibt im Postfach erhalten.";
        case "anruf":
            return "Diesen Anruf aus diesem Vorgang bzw. To-do entfernen? Andere Zuordnungen bleiben. Der Anruf selbst bleibt in der Telefonie erhalten.";
        case "dokument":
            // Bei einem Gast-Dokument wird nur die Anzeige hier gelöst; es ist
            // anderswo zu Hause und bleibt dort unberührt. Den Vorgang beim
            // Namen zu nennen, nimmt die Sorge, man räume ihn mit ab.
            if (opts.heimat !== undefined) {
                return opts.heimat
                    ? `Dieses Dokument aus dem Vorgang entfernen? Es bleibt in Vorgang ${opts.heimat.vorgangNbr ?? "seiner Heimat"} erhalten.`
                    : "Dieses Dokument aus dem Vorgang entfernen? Es bleibt in der Kundenakte erhalten.";
            }
            return opts.bleibtBeimKunden
                ? "Dieses Dokument aus dem Vorgang entfernen? Die Datei bleibt in der Kundenakte erhalten."
                : "Dieses Dokument aus dem Vorgang entfernen? Die Datei bleibt erhalten und ist über die Dokumentensuche erreichbar.";
        case "termin":
            return "Diesen Termin aus dem Vorgang entfernen? Der Termin selbst bleibt im Kalender erhalten.";
    }
}

/**
 * Die Rückfrage vor dem Löschen eines Dokuments (PO 15.09.2026, Wortlaut
 * nachgezogen mit E2 vom 16.09.2026).
 *
 * Sie steht im Menü direkt unter „Aus dem Vorgang entfernen", und die beiden
 * tun grundverschiedene Dinge: Entfernen löst nur die Verknüpfung und lässt die
 * Datei in der Kundenakte, Löschen nimmt sie aus allen Listen. Der Text nennt
 * deshalb beides — was passiert, und welcher Punkt gemeint war, falls man sich
 * vergriffen hat. Genau dort ist die Verwechslung teuer.
 *
 * `DELETE /documents/:id` wird mit E2 zum PAPIERKORB umgebaut (weich,
 * wiederherstellbar). Der Satz verspricht deshalb weder „sofort weg" noch einen
 * Wiederherstellen-Knopf, den es in der Oberfläche erst danach gibt: er sagt,
 * was in beiden Zuständen stimmt — die Datei verschwindet aus den Listen.
 */
export function loeschenRueckfrage(dateiname: string): string {
    return (
        `„${dateiname}" in den Papierkorb legen?\n\n` +
        "Die Datei verschwindet damit aus dem Vorgang und aus allen " +
        "Dokumentenlisten. Soll sie nur aus diesem Vorgang verschwinden, in der " +
        "Kundenakte aber bleiben, brich hier ab und wähle " +
        "„Aus dem Vorgang entfernen“."
    );
}

/**
 * Die Meldung nach „Anhänge übernehmen“ (PO 16.09.2026: „3 Anhänge übernommen,
 * 1 war schon da“).
 *
 * Der Import ist idempotent — eine Marke in `tags` erkennt bereits abgelegte
 * Anhänge wieder (`mail.controller.ts`, `mail-att:<messageId>:<attachmentId>`).
 * Die übersprungenen sind deshalb kein Fehler, sondern der Normalfall beim
 * zweiten Klick, und müssen als solcher dastehen: „nichts passiert“ ohne Grund
 * ließe den Nutzer ein drittes Mal klicken.
 *
 * Fehlgeschlagene Anhänge meldet der Endpoint EINZELN (`status: "error"`), ohne
 * den ganzen Aufruf scheitern zu lassen — sie zählen hier mit, sonst verschwiege
 * die Erfolgsmeldung, dass ein Anhang fehlt.
 */
export function anhaengeMeldung(summary: {
    imported: number;
    skipped: number;
    results: ReadonlyArray<{ status: "created" | "duplicate" | "linked" | "error" }>;
}): string {
    const fehler = summary.results.filter((r) => r.status === "error").length;
    // T-18: `linked` zählt das Backend unter `skipped` mit — die Datei lag
    // schon in der Kundenakte und wird hier nur angezeigt, nicht kopiert.
    const verknuepft = summary.results.filter((r) => r.status === "linked").length;
    const schonDa = Math.max(0, summary.skipped - verknuepft);
    const teile: string[] = [];
    if (summary.imported > 0) {
        teile.push(`${summary.imported} ${summary.imported === 1 ? "Anhang" : "Anhänge"} übernommen`);
    }
    if (verknuepft > 0) {
        teile.push(
            `${verknuepft} ${verknuepft === 1 ? "lag" : "lagen"} schon in der Kundenakte und ${
                verknuepft === 1 ? "ist" : "sind"
            } jetzt im Vorgang verknüpft`,
        );
    }
    if (schonDa > 0) {
        teile.push(`${schonDa} ${schonDa === 1 ? "war" : "waren"} schon da`);
    }
    if (fehler > 0) {
        teile.push(`${fehler} ${fehler === 1 ? "ließ" : "ließen"} sich nicht übernehmen`);
    }
    return teile.length > 0 ? `${teile.join(", ")}.` : "Es gab nichts zu übernehmen.";
}

/**
 * Die Dokument-Ids, die der Import NEU angelegt hat.
 *
 * Gebraucht, um die frischen Anhänge an dasselbe To-do zu hängen wie die Mail,
 * aus der sie stammen: `POST /mail/attachments/import` kennt nur `vorgangId`,
 * legt also am Vorgang ab. Hat der Vorgang To-dos, hat er keinen eigenen Strang
 * mehr (A4) — die Anhänge stünden dann in keiner Karte. Übersprungene zählen
 * nicht mit: die liegen schon irgendwo und werden nicht umgehängt.
 */
export function neueDokumentIds(summary: {
    results: ReadonlyArray<{ status: "created" | "duplicate" | "linked" | "error"; documentId: number | null }>;
}): number[] {
    return summary.results
        .filter((r) => r.status === "created" && r.documentId != null)
        .map((r) => r.documentId as number);
}

/**
 * Der Fehlertext für den Umbenennen-Dialog. `documentService.update` wirft
 * `Update failed (400): {"statusCode":400,"message":"Ungültiger Dateiname",…}`
 * — der Nutzer soll den Satz des Backends sehen, nicht das JSON darum. Alles,
 * was kein 400 mit lesbarer `message` ist, wird zum allgemeinen Satz.
 */
export function umbenennenFehlertext(err: unknown): string {
    const roh = err instanceof Error ? err.message : String(err ?? "");
    const klammer = roh.indexOf("{");
    if (roh.includes("(400)") && klammer >= 0) {
        try {
            const body = JSON.parse(roh.slice(klammer)) as { message?: unknown };
            const m = Array.isArray(body.message) ? body.message.join(", ") : body.message;
            if (typeof m === "string" && m.trim()) return m.trim();
        } catch {
            /* kein JSON — unten der allgemeine Satz */
        }
    }
    return "Umbenennen fehlgeschlagen.";
}
